/**
 * Timezone helpers for ONLINE (video-call) bookings on the marketplace.
 *
 * The backend computes ONLINE availability against the SALON's working hours
 * and returns bare "HH:mm" wall-clock strings in the SALON's timezone (see
 * SlotCalculator.availableOnlineSlots). It never converts to the customer's
 * zone. A customer in a different timezone therefore reads e.g. "14:00" as
 * their own 2pm when it is really 2pm at the salon — and books the wrong hour.
 *
 * These helpers convert a salon-local wall-clock time to the customer's zone
 * for DISPLAY ONLY. The booking payload still sends the salon-local time the
 * backend expects; nothing here changes what is submitted.
 *
 * No timezone library — everything is derived from Intl.DateTimeFormat, which
 * is available in every supported browser.
 */

/** Return true if the string is a valid IANA zone the runtime can format. */
export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    // Throws RangeError for an unknown/invalid zone.
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The offset (in minutes, East-of-UTC positive) of `timeZone` at the given
 * UTC instant. e.g. Europe/Brussels in summer → +120.
 *
 * Works by formatting the instant AS IF in the zone, reading back the wall
 * clock, and diffing against the same instant read as UTC.
 */
function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // The wall-clock the zone shows for this instant, as a UTC timestamp.
  const asUTC = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  );
  return Math.round((asUTC - instant.getTime()) / 60000);
}

/**
 * Interpret `date` (YYYY-MM-DD) + `time` (HH:mm) as a wall clock in
 * `timeZone`, and return the corresponding absolute UTC instant.
 *
 * DST-safe: we take the naive wall clock as a first UTC guess, measure the
 * zone offset there, subtract it, then re-measure once (the offset can differ
 * across a DST boundary). Two passes converge for all real-world zones.
 */
export function wallClockToInstant(
  date: string,
  time: string,
  timeZone: string
): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m || !t) return null;
  const [, y, mo, d] = m.map(Number);
  const [, hh, mm] = t.map(Number);

  const naiveUTC = Date.UTC(y, mo - 1, d, hh, mm);
  let offset = zoneOffsetMinutes(new Date(naiveUTC), timeZone);
  let instant = naiveUTC - offset * 60000;
  // Re-measure at the candidate instant to settle DST edges.
  const offset2 = zoneOffsetMinutes(new Date(instant), timeZone);
  if (offset2 !== offset) {
    offset = offset2;
    instant = naiveUTC - offset * 60000;
  }
  return new Date(instant);
}

export interface ConvertedSlot {
  /** Original salon-local time, unchanged (this is what gets booked). */
  salonTime: string;
  /** Time shown to the customer, in their zone: "HH:mm". */
  displayTime: string;
  /**
   * Day shift vs the picked (salon-local) date, from the customer's point of
   * view: -1 = previous day, 0 = same day, +1 = next day. Lets the UI mark a
   * slot that rolls over midnight in the customer's zone.
   */
  dayOffset: -1 | 0 | 1;
}

/**
 * Convert a salon-local "HH:mm" slot to the customer's zone for display.
 *
 * Returns null when we can't safely convert (invalid zones, unparseable
 * input) — the caller should then fall back to showing the salon-local time
 * with a "salon's local time" label rather than risk showing a wrong time.
 */
export function convertSlotForDisplay(
  date: string,
  salonTime: string,
  salonTimeZone: string,
  customerTimeZone: string
): ConvertedSlot | null {
  if (!isValidTimeZone(salonTimeZone) || !isValidTimeZone(customerTimeZone)) {
    return null;
  }
  const instant = wallClockToInstant(date, salonTime, salonTimeZone);
  if (!instant) return null;

  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: customerTimeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = dtf.formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const displayTime = `${get('hour')}:${get('minute')}`;

  // Day shift: compare the customer-zone calendar date to the salon-local date.
  const [sy, sm, sd] = date.split('-').map(Number);
  const cy = Number(get('year'));
  const cmo = Number(get('month'));
  const cd = Number(get('day'));
  const salonDay = Date.UTC(sy, sm - 1, sd);
  const custDay = Date.UTC(cy, cmo - 1, cd);
  const diffDays = Math.round((custDay - salonDay) / 86400000);
  const dayOffset: -1 | 0 | 1 = diffDays < 0 ? -1 : diffDays > 0 ? 1 : 0;

  return { salonTime, displayTime, dayOffset };
}

/**
 * Short human label for a timezone, e.g. "GMT+2" or the short name the
 * runtime provides ("CEST"). Used to tag the slot grid so the customer knows
 * which zone the times are in.
 */
export function shortZoneLabel(
  timeZone: string,
  referenceInstant: Date
): string {
  if (!isValidTimeZone(timeZone)) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
      hour: '2-digit',
    }).formatToParts(referenceInstant);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}
